import React, { useState, useEffect } from "react";
import SearchFieldRow from "./SearchFieldRow";
import DateSearchRow from "./DateSearchRow";

/**
 * 計測部門用 詳細検索フォームコンポーネント
 */
const KeisokuForm = ({ onFormChange, criteria, document_category = [], document_type = [] }) => {

    /**
     * 初期値（リセット時にも使用）
     * すべての入力項目・条件・あいまい検索 ON を含む
     */
    const defaultFormData = {
        // --- テキスト入力項目 ---
        auto_number: "",
        category_name: "",
        product_name: "",
        product_model: "",
        purpose: "",
        customer: "",
        assignee: "",
        original_doc_number: "",
        remarks: "",
        title: "",

        // --- 条件選択（デフォルト：0＝を含む） ---
        auto_number_condition: "0",
        category_name_condition: "0",
        product_name_condition: "0",
        product_model_condition: "0",
        purpose_condition: "0",
        customer_condition: "0",
        assignee_condition: "0",
        original_doc_number_condition: "0",
        remarks_condition: "0",
        title_condition: "0",

        // --- あいまい検索（デフォルト：ON） ---
        auto_number_fuzzy: true,
        category_name_fuzzy: true,
        product_name_fuzzy: true,
        product_model_fuzzy: true,
        purpose_fuzzy: true,
        customer_fuzzy: true,
        assignee_fuzzy: true,
        original_doc_number_fuzzy: true,
        remarks_fuzzy: true,
        title_fuzzy: true,

        // --- セレクト項目 ---
        document_category: "",
        document_type: "",
    };

    // 全フォーム値
    const [formData, setFormData] = useState(defaultFormData);

    /**
     * 初回マウント時：formData の全項目を親へ送信
     */
    useEffect(() => {
        if (onFormChange) {
            Object.entries(formData).forEach(([key, value]) => {
                onFormChange(key, value);
            });
        }
    // eslint-disable-next-line 
    }, []);

    /**
     * 親の criteria が更新されたときの処理
     * - criteria が初期状態（キー2つだけ）のとき → フォームをリセット
     * - それ以外 → criteria の値をフォームにマージ
     */
    useEffect(() => {

        // 初期状態 → リセット
        if (criteria && Object.keys(criteria).length <= 2) {
            setFormData(defaultFormData);

            Object.entries(defaultFormData).forEach(([k, v]) =>
                onFormChange(k, v)
            );
            return;
        }

        // API ロードなど → 既存フォームにマージ
        if (criteria && Object.keys(criteria).length > 2) {
            setFormData((prev) => ({ ...prev, ...criteria }));
        }
    // eslint-disable-next-line 
    }, [criteria]);

    // 全角＝1文字、半角＝0.5文字でカウント
    const trimJapaneseByLength = (str, limit) => {
        let result = "";
        let length = 0;

        for (const ch of str) {
            const add = ch.match(/[!-~]/) ? 0.5 : 1;
            if (length + add > limit) break;
            result += ch;
            length += add;
        }
        return result;
    };

    // 全角英数字 → 半角英数字
    const toHankaku = (str) =>
        str.replace(/[！-～]/g, (ch) =>
            String.fromCharCode(ch.charCodeAt(0) - 0xFEE0)
    );
    /**
     * 
     * 入力変更処理（全項目共通）
     * - 各フィールドごとの入力制限
     * - 半角変換ルール
     * - 親への onFormChange 通知
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        switch (name) {
            case "auto_number":
                // 半角英数字＋ハイフンのみ、最大8文字
                newValue = toHankaku(value)
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                break;

            case "category_name":
                // 全角15文字 → 半角なら最大30
                newValue = trimJapaneseByLength(value, 15);
                break;

            case "product_name":
            case "product_model":
            case "assignee":
            case "original_doc_number":
                // 全角10文字 → 半角なら最大20
                newValue = trimJapaneseByLength(value, 10);
                break;

            case "purpose":
            case "customer":
            case "remarks":
                // 全角20文字 → 半角なら最大40
                newValue = trimJapaneseByLength(value, 20);
                break;

            case "title":
                newValue = trimJapaneseByLength(value, 64);
                break;

            default:
                break;
        }

        const updated = { ...formData, [name]: newValue };
        setFormData(updated);
        onFormChange && onFormChange(name, newValue);
    };

    return (
        <div className="detail-grid-2col">

            {/* ───── 左カラム ───── */}
            <div className="form-column">

                {/* オートナンバー */}
                <div className="form-row">
                    <SearchFieldRow
                        label="オートナンバー"
                        name="auto_number"
                        value={formData.auto_number}
                        onChange={handleChange}
                        condition={formData.auto_number_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData((prev) => ({ ...prev, auto_number_condition: val }));
                            onFormChange("auto_number_condition", val);
                        }}
                        isFuzzy={formData.auto_number_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData((prev) => ({ ...prev, auto_number_fuzzy: checked }));
                            onFormChange("auto_number_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 図面種類 */}
                <div className="form-row">
                    <div className="search-field">
                        <label>図面種類（計測）</label>
                        <select
                            name="document_category"
                            value={formData.document_category || ""}
                            onChange={handleChange}
                            style={{ flex: '1', width: '100%', border: '1px solid #fff' }}
                        >
                            <option value=""></option>
                            {document_category?.map((item) => (
                                <option key={item.display_idx} value={item.document_category}>
                                    {item.document_category_text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 以下、他の SearchFieldRow も同様（コメント省略しない版） */}
                {/* 図面名称 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="図面名称"
                        name="category_name"
                        value={formData.category_name}
                        onChange={handleChange}
                        condition={formData.category_name_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, category_name_condition: val }));
                            onFormChange("category_name_condition", val);
                        }}
                        isFuzzy={formData.category_name_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, category_name_fuzzy: checked }));
                            onFormChange("category_name_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 製品名 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="製品名"
                        name="product_name"
                        value={formData.product_name}
                        onChange={handleChange}
                        condition={formData.product_name_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, product_name_condition: val }));
                            onFormChange("product_name_condition", val);
                        }}
                        isFuzzy={formData.product_name_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, product_name_fuzzy: checked }));
                            onFormChange("product_name_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 製品形式 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="製品形式"
                        name="product_model"
                        value={formData.product_model}
                        onChange={handleChange}
                        condition={formData.product_model_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, product_model_condition: val }));
                            onFormChange("product_model_condition", val);
                        }}
                        isFuzzy={formData.product_model_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, product_model_fuzzy: checked }));
                            onFormChange("product_model_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 用途 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="用途"
                        name="purpose"
                        value={formData.purpose}
                        onChange={handleChange}
                        condition={formData.purpose_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, purpose_condition: val }));
                            onFormChange("purpose_condition", val);
                        }}
                        isFuzzy={formData.purpose_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, purpose_fuzzy: checked }));
                            onFormChange("purpose_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 得意先 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="得意先"
                        name="customer"
                        value={formData.customer}
                        onChange={handleChange}
                        condition={formData.customer_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, customer_condition: val }));
                            onFormChange("customer_condition", val);
                        }}
                        isFuzzy={formData.customer_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, customer_fuzzy: checked }));
                            onFormChange("customer_fuzzy", checked);
                        }}
                    />
                </div>
            </div>


            {/* ───── 右カラム ───── */}
            <div className="form-column">

                {/* 日付検索 */}
                <div className="form-row">
                    <DateSearchRow
                        onChange={(value) => {
                            onFormChange("dateCondition", value);
                        }}
                    />
                </div>

                {/* 担当者 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="担当者"
                        name="assignee"
                        value={formData.assignee}
                        onChange={handleChange}
                        condition={formData.assignee_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, assignee_condition: val }));
                            onFormChange("assignee_condition", val);
                        }}
                        isFuzzy={formData.assignee_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, assignee_fuzzy: checked }));
                            onFormChange("assignee_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 図面タイプ */}
                <div className="form-row">
                    <div className="search-field">
                        <label>図面タイプ（計測）</label>
                        <select
                            name="document_type"
                            value={formData.document_type || ""}
                            onChange={handleChange}
                            style={{ flex: '1', width: '100%', border: '1px solid #fff' }}
                        >
                            <option value=""></option>
                            {document_type?.map((item) => (
                                <option key={item.display_idx} value={item.document_type}>
                                    {item.document_type_text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 元図番 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="元図番"
                        name="original_doc_number"
                        value={formData.original_doc_number}
                        onChange={handleChange}
                        condition={formData.original_doc_number_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, original_doc_number_condition: val }));
                            onFormChange("original_doc_number_condition", val);
                        }}
                        isFuzzy={formData.original_doc_number_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, original_doc_number_fuzzy: checked }));
                            onFormChange("original_doc_number_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 備考 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="備考"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        condition={formData.remarks_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, remarks_condition: val }));
                            onFormChange("remarks_condition", val);
                        }}
                        isFuzzy={formData.remarks_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, remarks_fuzzy: checked }));
                            onFormChange("remarks_fuzzy", checked);
                        }}
                    />
                </div>

                {/* タイトル */}
                <div className="form-row">
                    <SearchFieldRow
                        label="タイトル"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        condition={formData.title_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, title_condition: val }));
                            onFormChange("title_condition", val);
                        }}
                        isFuzzy={formData.title_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, title_fuzzy: checked }));
                            onFormChange("title_fuzzy", checked);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default KeisokuForm;
